
// DispatcherNotifications.js
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import api from "../axiosConfig";
import "../styles/DispatcherNotifications.css";
import { getSocket } from "../lib/socket";
import {
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiCheckCircle,
  FiXCircle,
  FiSearch,
  FiBell,
  FiMail,
  FiClock,
  FiMapPin,
  FiUser,
  FiX,
  FiChevronDown,
  FiFilter,
  FiBox,
  FiCamera,
  FiFileText,
} from "react-icons/fi";
import { LuTruck } from "react-icons/lu";
import { useTranslation } from "react-i18next";

const NOTIFS_KEY = "app.notifications";

function getNotifsActive() {
  const v = window.localStorage.getItem(NOTIFS_KEY);
  return v ? v === "active" : true; // default = ativo
}

function normalizeNotificationDateParams(params = {}) {
  const next = {
    ...params,
  };

  const dateMappings = [
    ["changedAt", "changedAtFormatted"],
    ["assignedAt", "assignedAtFormatted"],
    ["unassignedAt", "unassignedAtFormatted"],
    ["updatedAt", "updatedAtFormatted"],
    ["deletedAt", "deletedAtFormatted"],
    ["createdAt", "createdAtFormatted"],
    ["revertedAt", "revertedAtFormatted"],
    ["respondedAt", "respondedAtFormatted"],
  ];

  dateMappings.forEach(([sourceKey, formattedKey]) => {
    const sourceValue = next[sourceKey];

    if (!sourceValue) return;

    const formattedValue = formatDateTime(sourceValue);

    if (!formattedValue) return;

    next[sourceKey] = formattedValue;
    next[formattedKey] = formattedValue;
  });

  return next;
}

function formatDateTime(value) {
  if (!value) return "";

  const raw = String(value).trim();

  // Evita tentar converter novamente uma data já formatada pela interface.
  if (/^\d{2}-\d{2}-\d{4}\s\d{2}:\d{2}$/.test(raw)) {
    return raw;
  }

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) return "";

  // Usa automaticamente o timezone local do navegador.
  const dia = String(d.getDate()).padStart(2, "0");
  const mes = String(d.getMonth() + 1).padStart(2, "0");
  const ano = d.getFullYear();
  const horas = String(d.getHours()).padStart(2, "0");
  const mins = String(d.getMinutes()).padStart(2, "0");

  return `${dia}-${mes}-${ano} ${horas}:${mins}`;
}

function formatDateOnly(value) {
  if (!value) return "";
  const s = String(value);
  // se vier ISO, usamos só YYYY-MM-DD (sem timezone shifts)
  const ymd = s.includes("T") ? s.slice(0, 10) : s.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}-${m}-${y}`;
}
function parseMeta(meta) {
  if (!meta) return null;
  if (typeof meta === "object") return meta;
  if (typeof meta !== "string") return null;
  const s = meta.trim();
  if (!s) return null;
  // tenta JSON.parse se parecer JSON
  if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
    try { return JSON.parse(s); } catch (_) { return null; }
  }
  return null;
}

function normalizeNotificationType(value) {
  return String(value || "")
    .trim()
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();
}

function getNotificationParams(meta = {}) {
  const params = meta?.i18nParams || meta?.i18n?.args || {};
  return {
    ...meta,
    ...params,
  };
}

function isStatusUpdateNotification(notif, meta = {}) {
  const normalizedType = normalizeNotificationType(notif?.notificationType);
  const params = getNotificationParams(meta);
  const key = String(meta?.i18nKey || meta?.i18n?.baseKey || "").toLowerCase();

  return (
    normalizedType.includes("STATUS") ||
    normalizedType === "DRIVERSTATUSCHANGED" ||
    normalizedType === "POINTSTATUSUPDATED" ||
    normalizedType === "LOADSTATUSUPDATED" ||
    key.includes("statusupdated") ||
    key.includes("statuschanged") ||
    Boolean(params.newStatus || params.newStatusLabel || params.pointStatus)
  );
}

function getNotificationLoadNumber(notif, meta = {}) {
  const params = getNotificationParams(meta);

  return (
    params.loadNumber ||
    params.loadId ||
    params.carrierLoadId ||
    notif?.loadNumber ||
    notif?.loadId ||
    null
  );
}

function getNotificationPointType(meta = {}) {
  const params = getNotificationParams(meta);

  return (
    params.pointType ||
    params.type ||
    params.scope ||
    params.pointScope ||
    null
  );
}

function getNotificationLocation(meta = {}) {
  const params = getNotificationParams(meta);

  return (
    params.location ||
    params.pointLocation ||
    params.pickupLocation ||
    params.deliveryLocation ||
    params.pickupInfo ||
    params.deliveryInfo ||
    null
  );
}

function getNotificationNewStatus(meta = {}) {
  const params = getNotificationParams(meta);

  return (
    params.newStatusLabel ||
    params.newStatus ||
    params.statusLabel ||
    params.status ||
    params.pointStatusLabel ||
    params.pointStatus ||
    null
  );
}

function getNotificationResponsible(meta = {}) {
  const params = getNotificationParams(meta);

  return (
    params.actorName ||
    params.updatedByName ||
    params.deletedByUserName ||
    params.driverName ||
    params.createdByUserName ||
    null
  );
}

const STATUS_ALIAS_KEYS = {
  WAITING_FOR_MERCHANDISE: "WAITING_FOR_MERCHANDISE",
  WAITING_MERCHANDISE: "WAITING_FOR_MERCHANDISE",

  MOVING_TO_PICKUP_LOCATION: "MOVING_TO_PICKUP_LOCATION",
  MOVING_PICKUP_LOCATION: "MOVING_TO_PICKUP_LOCATION",

  AT_PICKUP_LOCATION: "AT_PICKUP_LOCATION",

  LOADING_MERCHANDISE: "LOADING_MERCHANDISE",

  LOADING_DELAYED_SCHEDULING: "LOADING_DELAYED_SCHEDULING",
  LOADING_SCHEDULING_DELAYED: "LOADING_DELAYED_SCHEDULING",
  LOADING_DELAYED_SCHEDULE: "LOADING_DELAYED_SCHEDULING",

  LOADING_DELAYED_DOCS: "LOADING_DELAYED_DOCS",
  LOADING_DELAYED_DOCUMENTS: "LOADING_DELAYED_DOCS",
  LOADING_DELAYED_DOCUMENTATION: "LOADING_DELAYED_DOCS",
  LOADING_DOCUMENTS_DELAYED: "LOADING_DELAYED_DOCS",
  LOADING_DOCS_DELAYED: "LOADING_DELAYED_DOCS",

  IN_TRANSIT_TO_DELIVERY: "IN_TRANSIT_TO_DELIVERY",
  AT_DELIVERY_LOCATION: "AT_DELIVERY_LOCATION",
  UNLOADING_MERCHANDISE: "UNLOADING_MERCHANDISE",

  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  CANCELED: "CANCELED",
  DELAYED: "DELAYED",

  WAITING_DELAYED_LATE_TRUCK: "WAITING_DELAYED_LATE_TRUCK",
  WAITING_TRUCK_DELAYED: "WAITING_DELAYED_LATE_TRUCK",

  WAITING_DELAYED_LATE_TRAILER: "WAITING_DELAYED_LATE_TRAILER",
  WAITING_TRAILER_DELAYED: "WAITING_DELAYED_LATE_TRAILER",

  WAITING_DELAYED_LATE_DRIVER: "WAITING_DELAYED_LATE_DRIVER",
  WAITING_DRIVER_DELAYED: "WAITING_DELAYED_LATE_DRIVER",

  WAITING_DELAYED_LATE_TRUCK_AND_TRAILER:
    "WAITING_DELAYED_LATE_TRUCK_AND_TRAILER",
  WAITING_TRUCK_AND_TRAILER_DELAYED:
    "WAITING_DELAYED_LATE_TRUCK_AND_TRAILER",

  WAITING_DELAYED_LATE_DRIVER_AND_TRUCK:
    "WAITING_DELAYED_LATE_DRIVER_AND_TRUCK",
  WAITING_DRIVER_AND_TRUCK_DELAYED:
    "WAITING_DELAYED_LATE_DRIVER_AND_TRUCK",

  WAITING_DELAYED_LATE_DRIVER_AND_TRAILER:
    "WAITING_DELAYED_LATE_DRIVER_AND_TRAILER",
  WAITING_DRIVER_AND_TRAILER_DELAYED:
    "WAITING_DELAYED_LATE_DRIVER_AND_TRAILER",

  WAITING_DELAYED_LATE_ALL: "WAITING_DELAYED_LATE_ALL",
  WAITING_RESOURCES_DELAYED: "WAITING_DELAYED_LATE_ALL",

  WAITING_DELAYED_TRAILER_INCOMPATIBLE:
    "WAITING_DELAYED_TRAILER_INCOMPATIBLE",
  WAITING_TRAILER_INCOMPATIBLE:
    "WAITING_DELAYED_TRAILER_INCOMPATIBLE",
  WAITING_DELAYED_INCOMPATIBLE_TRAILER:
    "WAITING_DELAYED_TRAILER_INCOMPATIBLE",
  WAITING_DELAYED_TRAILER_NOT_COMPATIBLE:
    "WAITING_DELAYED_TRAILER_INCOMPATIBLE",
};

const STATUS_TEXT_REPLACEMENTS = [
  ["Waiting Delayed Trailer Incompatible", "WAITING_DELAYED_TRAILER_INCOMPATIBLE"],
  ["Waiting Delayed Late Truck And Trailer", "WAITING_DELAYED_LATE_TRUCK_AND_TRAILER"],
  ["Waiting Delayed Late Driver And Trailer", "WAITING_DELAYED_LATE_DRIVER_AND_TRAILER"],
  ["Waiting Delayed Late Driver And Truck", "WAITING_DELAYED_LATE_DRIVER_AND_TRUCK"],
  ["Waiting Delayed Late Trailer", "WAITING_DELAYED_LATE_TRAILER"],
  ["Waiting Delayed Late Driver", "WAITING_DELAYED_LATE_DRIVER"],
  ["Waiting Delayed Late Truck", "WAITING_DELAYED_LATE_TRUCK"],
  ["Waiting Delayed Late All", "WAITING_DELAYED_LATE_ALL"],

  ["Waiting Truck And Trailer Delayed", "WAITING_DELAYED_LATE_TRUCK_AND_TRAILER"],
  ["Waiting Driver And Trailer Delayed", "WAITING_DELAYED_LATE_DRIVER_AND_TRAILER"],
  ["Waiting Driver And Truck Delayed", "WAITING_DELAYED_LATE_DRIVER_AND_TRUCK"],
  ["Waiting Trailer Incompatible", "WAITING_DELAYED_TRAILER_INCOMPATIBLE"],
  ["Waiting Trailer Delayed", "WAITING_DELAYED_LATE_TRAILER"],
  ["Waiting Driver Delayed", "WAITING_DELAYED_LATE_DRIVER"],
  ["Waiting Truck Delayed", "WAITING_DELAYED_LATE_TRUCK"],
  ["Waiting Resources Delayed", "WAITING_DELAYED_LATE_ALL"],

  ["Loading Delayed Scheduling", "LOADING_DELAYED_SCHEDULING"],
  ["Loading Scheduling Delayed", "LOADING_DELAYED_SCHEDULING"],
  ["Loading Delayed Schedule", "LOADING_DELAYED_SCHEDULING"],

  ["Loading Delayed Documents", "LOADING_DELAYED_DOCS"],
  ["Loading Delayed Documentation", "LOADING_DELAYED_DOCS"],
  ["Loading Documents Delayed", "LOADING_DELAYED_DOCS"],
  ["Loading Docs Delayed", "LOADING_DELAYED_DOCS"],
  ["Loading Delayed Docs", "LOADING_DELAYED_DOCS"],

  ["Waiting For Merchandise", "WAITING_FOR_MERCHANDISE"],
  ["Moving To Pickup Location", "MOVING_TO_PICKUP_LOCATION"],
  ["At Pickup Location", "AT_PICKUP_LOCATION"],
  ["Loading Merchandise", "LOADING_MERCHANDISE"],
  ["In Transit To Delivery", "IN_TRANSIT_TO_DELIVERY"],
  ["At Delivery Location", "AT_DELIVERY_LOCATION"],
  ["Unloading Merchandise", "UNLOADING_MERCHANDISE"],
  ["Moving To Delivery Location", "MOVING_TO_DELIVERY_LOCATION"],
  ["In Transit", "IN_TRANSIT"],

  ["Completed", "COMPLETED"],
  ["Cancelled", "CANCELLED"],
  ["Canceled", "CANCELED"],
  ["Delayed", "DELAYED"],
];

const STATUS_LABEL_DEFAULTS = {
  pt: {
    WAITING_FOR_MERCHANDISE: "A AGUARDAR MERCADORIA",
    MOVING_TO_PICKUP_LOCATION: "A CAMINHO DO PONTO DE CARGA",
    AT_PICKUP_LOCATION: "NO PONTO DE CARGA",
    LOADING_MERCHANDISE: "A CARREGAR MERCADORIA",
    LOADING_DELAYED_SCHEDULING: "A CARREGAR — ATRASO NO AGENDAMENTO",
    LOADING_DELAYED_DOCS: "A CARREGAR — DOCUMENTOS ATRASADOS",
    IN_TRANSIT_TO_DELIVERY: "EM TRÂNSITO PARA DESCARGA",
    AT_DELIVERY_LOCATION: "NO PONTO DE DESCARGA",
    UNLOADING_MERCHANDISE: "A DESCARREGAR MERCADORIA",
    COMPLETED: "CONCLUÍDO",
    CANCELLED: "CANCELADO",
    CANCELED: "CANCELADO",
    DELAYED: "ATRASADO",
    WAITING_DELAYED_LATE_TRUCK: "A AGUARDAR — CAMIÃO ATRASADO",
    WAITING_DELAYED_LATE_TRAILER: "A AGUARDAR — REBOQUE ATRASADO",
    WAITING_DELAYED_LATE_DRIVER: "A AGUARDAR — MOTORISTA ATRASADO",
    WAITING_DELAYED_LATE_TRUCK_AND_TRAILER:
      "A AGUARDAR — CAMIÃO E REBOQUE ATRASADOS",
    WAITING_DELAYED_LATE_DRIVER_AND_TRUCK:
      "A AGUARDAR — MOTORISTA E CAMIÃO ATRASADOS",
    WAITING_DELAYED_LATE_DRIVER_AND_TRAILER:
      "A AGUARDAR — MOTORISTA E REBOQUE ATRASADOS",
    WAITING_DELAYED_LATE_ALL: "A AGUARDAR — RECURSOS ATRASADOS",
    WAITING_DELAYED_TRAILER_INCOMPATIBLE:
      "A AGUARDAR — REBOQUE INCOMPATÍVEL",
    IN_TRANSIT: "EM TRÂNSITO",
    MOVING_TO_DELIVERY_LOCATION: "A CAMINHO DO PONTO DE DESCARGA",
  },
  en: {
    WAITING_FOR_MERCHANDISE: "WAITING FOR MERCHANDISE",
    MOVING_TO_PICKUP_LOCATION: "MOVING TO PICKUP LOCATION",
    AT_PICKUP_LOCATION: "AT PICKUP LOCATION",
    LOADING_MERCHANDISE: "LOADING MERCHANDISE",
    LOADING_DELAYED_SCHEDULING: "LOADING — SCHEDULING DELAYED",
    LOADING_DELAYED_DOCS: "LOADING — DOCUMENTS DELAYED",
    IN_TRANSIT_TO_DELIVERY: "IN TRANSIT TO DELIVERY",
    AT_DELIVERY_LOCATION: "AT DELIVERY LOCATION",
    UNLOADING_MERCHANDISE: "UNLOADING MERCHANDISE",
    COMPLETED: "COMPLETED",
    CANCELLED: "CANCELLED",
    CANCELED: "CANCELLED",
    DELAYED: "DELAYED",
    WAITING_DELAYED_LATE_TRUCK: "WAITING — TRUCK DELAYED",
    WAITING_DELAYED_LATE_TRAILER: "WAITING — TRAILER DELAYED",
    WAITING_DELAYED_LATE_DRIVER: "WAITING — DRIVER DELAYED",
    WAITING_DELAYED_LATE_TRUCK_AND_TRAILER:
      "WAITING — TRUCK AND TRAILER DELAYED",
    WAITING_DELAYED_LATE_DRIVER_AND_TRUCK:
      "WAITING — DRIVER AND TRUCK DELAYED",
    WAITING_DELAYED_LATE_DRIVER_AND_TRAILER:
      "WAITING — DRIVER AND TRAILER DELAYED",
    WAITING_DELAYED_LATE_ALL: "WAITING — RESOURCES DELAYED",
    WAITING_DELAYED_TRAILER_INCOMPATIBLE:
      "WAITING — TRAILER INCOMPATIBLE",
    IN_TRANSIT: "IN TRANSIT",
    MOVING_TO_DELIVERY_LOCATION: "MOVING TO DELIVERY LOCATION",
  },
  es: {
    WAITING_FOR_MERCHANDISE: "ESPERANDO MERCANCÍA",
    MOVING_TO_PICKUP_LOCATION: "EN CAMINO AL PUNTO DE CARGA",
    AT_PICKUP_LOCATION: "EN EL PUNTO DE CARGA",
    LOADING_MERCHANDISE: "CARGANDO MERCANCÍA",
    LOADING_DELAYED_SCHEDULING: "CARGANDO — PROGRAMACIÓN CON RETRASO",
    LOADING_DELAYED_DOCS: "CARGANDO — DOCUMENTOS CON RETRASO",
    IN_TRANSIT_TO_DELIVERY: "EN TRÁNSITO HACIA DESCARGA",
    AT_DELIVERY_LOCATION: "EN EL PUNTO DE DESCARGA",
    UNLOADING_MERCHANDISE: "DESCARGANDO MERCANCÍA",
    COMPLETED: "COMPLETADO",
    CANCELLED: "CANCELADO",
    CANCELED: "CANCELADO",
    DELAYED: "RETRASADO",
    WAITING_DELAYED_LATE_TRUCK: "EN ESPERA — CAMIÓN CON RETRASO",
    WAITING_DELAYED_LATE_TRAILER: "EN ESPERA — REMOLQUE CON RETRASO",
    WAITING_DELAYED_LATE_DRIVER: "EN ESPERA — CONDUCTOR CON RETRASO",
    WAITING_DELAYED_LATE_TRUCK_AND_TRAILER:
      "EN ESPERA — CAMIÓN Y REMOLQUE CON RETRASO",
    WAITING_DELAYED_LATE_DRIVER_AND_TRUCK:
      "EN ESPERA — CONDUCTOR Y CAMIÓN CON RETRASO",
    WAITING_DELAYED_LATE_DRIVER_AND_TRAILER:
      "EN ESPERA — CONDUCTOR Y REMOLQUE CON RETRASO",
    WAITING_DELAYED_LATE_ALL: "EN ESPERA — RECURSOS CON RETRASO",
    WAITING_DELAYED_TRAILER_INCOMPATIBLE:
      "EN ESPERA — REMOLQUE INCOMPATIBLE",
    IN_TRANSIT: "EN TRÁNSITO",
    MOVING_TO_DELIVERY_LOCATION: "EN CAMINO AL PUNTO DE DESCARGA",
  },
  fr: {
    WAITING_FOR_MERCHANDISE: "EN ATTENTE DE MARCHANDISE",
    MOVING_TO_PICKUP_LOCATION: "EN ROUTE VERS LE POINT DE CHARGEMENT",
    AT_PICKUP_LOCATION: "AU POINT DE CHARGEMENT",
    LOADING_MERCHANDISE: "CHARGEMENT DE LA MARCHANDISE",
    LOADING_DELAYED_SCHEDULING: "CHARGEMENT — PLANIFICATION EN RETARD",
    LOADING_DELAYED_DOCS: "CHARGEMENT — DOCUMENTS EN RETARD",
    IN_TRANSIT_TO_DELIVERY: "EN TRANSIT VERS LE DÉCHARGEMENT",
    AT_DELIVERY_LOCATION: "AU POINT DE DÉCHARGEMENT",
    UNLOADING_MERCHANDISE: "DÉCHARGEMENT DE LA MARCHANDISE",
    COMPLETED: "TERMINÉ",
    CANCELLED: "ANNULÉ",
    CANCELED: "ANNULÉ",
    DELAYED: "EN RETARD",
    WAITING_DELAYED_LATE_TRUCK: "EN ATTENTE — CAMION EN RETARD",
    WAITING_DELAYED_LATE_TRAILER: "EN ATTENTE — REMORQUE EN RETARD",
    WAITING_DELAYED_LATE_DRIVER: "EN ATTENTE — CHAUFFEUR EN RETARD",
    WAITING_DELAYED_LATE_TRUCK_AND_TRAILER:
      "EN ATTENTE — CAMION ET REMORQUE EN RETARD",
    WAITING_DELAYED_LATE_DRIVER_AND_TRUCK:
      "EN ATTENTE — CHAUFFEUR ET CAMION EN RETARD",
    WAITING_DELAYED_LATE_DRIVER_AND_TRAILER:
      "EN ATTENTE — CHAUFFEUR ET REMORQUE EN RETARD",
    WAITING_DELAYED_LATE_ALL: "EN ATTENTE — RESSOURCES EN RETARD",
    WAITING_DELAYED_TRAILER_INCOMPATIBLE:
      "EN ATTENTE — REMORQUE INCOMPATIBLE",
    IN_TRANSIT: "EN TRANSIT",
    MOVING_TO_DELIVERY_LOCATION: "EN ROUTE VERS LE POINT DE DÉCHARGEMENT",
  },
};

function normalizeStatusKey(value) {
  return String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[–—-]/g, " ")
    .replace(/&/g, " AND ")
    .replace(/\+/g, " AND ")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_")
    .toUpperCase();
}

function getI18nLang(i18n) {
  return String(i18n?.language || "pt")
    .split("-")[0]
    .trim()
    .toLowerCase();
}

function getDefaultStatusLabel(statusKey, i18n) {
  const lang = getI18nLang(i18n);
  return (
    STATUS_LABEL_DEFAULTS[lang]?.[statusKey] ||
    STATUS_LABEL_DEFAULTS.pt?.[statusKey] ||
    STATUS_LABEL_DEFAULTS.en?.[statusKey] ||
    null
  );
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renderNotifLabel(t, notif) {
  const m = parseMeta(notif?.meta);
  const key = m?.i18nKey;
  const params = m?.i18nParams || m || {};
  if (key) return t(key, params);
  // fallback
  return notif?.title || notif?.notificationType || "—";
}

function getNotificationVisual(notificationType) {
  const raw = String(notificationType || "");
  const normalizedType = normalizeNotificationType(raw);

  if (
    raw.startsWith("TRUCK_INSPECTION_PHOTO_") ||
    raw.startsWith("TRAILER_INSPECTION_PHOTO_") ||
    normalizedType.includes("INSPECTIONPHOTO")
  ) {
    return { Icon: FiCamera, variant: "purple" };
  }

  if (
    normalizedType.includes("PURCHASEORDER") ||
    normalizedType.includes("INVOICE") ||
    normalizedType.includes("VRC") ||
    normalizedType.includes("DOCUMENT")
  ) {
    return { Icon: FiFileText, variant: "amber" };
  }

  if (normalizedType.includes("SHIPPERCONNECTION")) {
    return { Icon: FiMail, variant: "green" };
  }

  if (
    normalizedType.includes("STATUS") ||
    normalizedType === "DRIVERSTATUSCHANGED" ||
    normalizedType === "POINTSTATUSUPDATED" ||
    normalizedType === "LOADSTATUSUPDATED"
  ) {
    return { Icon: FiBox, variant: "blue" };
  }

  const isResourceRemoval =
    normalizedType.includes("UNASSIGN") ||
    normalizedType.includes("DEASSIGN") ||
    normalizedType.includes("REMOV") ||
    normalizedType.includes("DETACH") ||
    normalizedType.includes("CLEAR");

  if (normalizedType.includes("TRUCK")) {
    return {
      Icon: LuTruck,
      variant: isResourceRemoval ? "truck-removed" : "truck",
    };
  }

  if (normalizedType.includes("TRAILER")) {
    return {
      Icon: LuTruck,
      variant: isResourceRemoval ? "trailer-removed" : "trailer",
    };
  }

  if (normalizedType.includes("DRIVER")) {
    return {
      Icon: FiUser,
      variant: isResourceRemoval ? "driver-removed" : "driver",
    };
  }

  return { Icon: FiBell, variant: "slate" };
}

function DispatcherNotifications() {
  const { t, i18n } = useTranslation("dispatcher_notifications");
  const [items, setItems] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [activeNotificationId, setActiveNotificationId] = useState(null);
  const [notificationsActive, setNotificationsActive] = useState(getNotifsActive);
  const [connectionRespondingIds, setConnectionRespondingIds] = useState(() => new Set());
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  /* ===================== Infinite scroll (20 + 20) ===================== */
  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const loadMoreRef = useRef(null);
  const listScrollRef = useRef(null);
  const filterMenuRef = useRef(null);
  // 🔐 Estado local (por navegador/computador) para "lidas" e "apagadas"
  const LOCAL_STORAGE_KEY = "dispatcherNotifPrefs";
  const [localState, setLocalState] = useState(() => ({
    readIds: new Set(), // ids marcados como lidos neste browser
    hiddenIds: new Set(), // ids "apagados" (escondidos) neste browser
  }));
  // Helper para ler/gravar no localStorage de forma segura
  const updateLocalState = useCallback((updater) => {
    setLocalState((prev) => {
      const next = updater(prev);
      try {
        const payload = {
          readIds: Array.from(next.readIds || []),
          hiddenIds: Array.from(next.hiddenIds || []),
        };
        window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
      } catch (e) {
        console.warn(
          "[DispatcherNotifications] falha ao gravar prefs locais",
          e
        );
      }
      return next;
    });
  }, []);

  // Carregar prefs locais na montagem
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setLocalState({
        readIds: new Set(parsed.readIds || []),
        hiddenIds: new Set(parsed.hiddenIds || []),
      });
    } catch (e) {
      console.warn("[DispatcherNotifications] falha ao ler prefs locais", e);
    }
  }, []);

  const fetchNotifications = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!getNotifsActive()) {
        setItems([]);
        setErr(null);
        setInitialLoading(false);
        setRefreshing(false);
        return;
      }

      // ✅ Só mostra skeleton no PRIMEIRO carregamento.
      // Refreshes seguintes atualizam em background, sem piscar a página.
      if (silent) {
        setRefreshing(true);
      } else {
        setInitialLoading((prev) => prev);
      }

      const PAGE_FETCH_SIZE = 500;
      let offset = 0;
      let allItems = [];
      let hasMore = true;

      while (hasMore) {
        const { data } = await api.get("/notifications4dispatcher", {
          params: {
            limit: PAGE_FETCH_SIZE,
            offset,
          },
        });

        const batch = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
          ? data
          : [];

        allItems = [...allItems, ...batch];
        hasMore = Boolean(data?.pagination?.hasMore);
        offset += batch.length;

        if (!batch.length) {
          hasMore = false;
        }
      }

      setItems(allItems);
      setErr(null);
    } catch (e) {
      console.error("[DispatcherNotifications] error loading", e);
      setErr("loadFailed");
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications({ silent: false });
  }, [fetchNotifications]);

  useEffect(() => {
    if (!notificationsActive) {
      // limpa UI e estados visuais quando OFF
      setItems([]);
      setErr(null);
      setInitialLoading(false);
      setRefreshing(false);
      setSelectedIds(() => new Set());
      setFilter("all");
      setVisibleCount(PAGE_SIZE);

      try {
        window.dispatchEvent(
          new CustomEvent("dispatcher:notifs:unreadCount", {
            detail: { unread: 0 },
          })
        );
      } catch (_) {}
    }
  }, [notificationsActive]);

  // WebSocket para novas notificações / stale
  useEffect(() => {
    // ✅ se estiver OFF, não liga socket nem subscreve rooms
    if (!notificationsActive) return;
    const socket = getSocket();
    console.log(
      "[DispatcherNotifications] socket ligado para notificações, id=",
      socket.id
    );
    // ✅ garante que estamos na room onde o backend emite
    const subscribe = () => {
      if (socket.connected) {
        socket.emit("subscribe:loads");
      }
    };
    socket.on("connect", subscribe);
    socket.on("reconnect", subscribe);
    if (socket.connected) {
      subscribe();
    }

    // quando backend emite dispatcher:notifications:new
    const handleNewNotification = (notif) => {
      console.log(
        "[DispatcherNotifications] dispatcher:notifications:new recebido",
        notif
      );

      if (!notif || typeof notif !== "object") return;

      setItems((prev) => {
        const exists = prev.some((n) => String(n.id) === String(notif.id));
        if (exists) return prev;

        // 🔊 Fallback global:
        // o GlobalNotificationListener apanha isto e toca som com dedupe.
        try {
          window.dispatchEvent(
            new CustomEvent("loadevo:dispatcher-notification:new", {
              detail: { notification: notif },
            })
          );
        } catch (e) {
          console.warn(
            "[DispatcherNotifications] failed to dispatch notification sound event",
            e
          );
        }

        return [notif, ...prev];
      });
    };

    // (opcional) se um dia emitires dispatcher:notifications:stale → refetch
    const handleStale = () => {
      console.log(
        "[DispatcherNotifications] dispatcher:notifications:stale → refetch"
      );
    
      if (!getNotifsActive()) return;
    
      fetchNotifications({ silent: true }).catch((e) =>
        console.error(
          "[DispatcherNotifications] erro ao refazer fetch após stale",
          e
        )
      );
    };
    socket.on("dispatcher:notifications:new", handleNewNotification);
    socket.on("dispatcher:notifications:stale", handleStale);
    return () => {
      // ✅ sair da room e limpar listeners de reconexão
      socket.emit("unsubscribe:loads");
      socket.off("connect", subscribe);
      socket.off("reconnect", subscribe);
      socket.off("dispatcher:notifications:new", handleNewNotification);
      socket.off("dispatcher:notifications:stale", handleStale);
    };
  }, [notificationsActive, fetchNotifications]);

  // 🔄 Aplica “hidden” e “read” locais ao array vindo do backend
  const itemsWithLocalState = useMemo(() => {
    const { readIds, hiddenIds } = localState;
    return items
      // 1️⃣ Esconder as que foram "apagadas" neste browser
      .filter((n) => !hiddenIds.has(n.id))
      // 2️⃣ Marcar como lidas localmente (se não tiverem readAt real)
      .map((n) =>
        !n.readAt && readIds.has(n.id)
          ? { ...n, readAt: "__local__" } // valor só para UI (truthy)
          : n
      );
  }, [items, localState]);

  const unreadCount = useMemo(
    () => itemsWithLocalState.filter((n) => !n.readAt).length,
    [itemsWithLocalState]
  );

  const filteredItems = useMemo(() => {
    let next = [...itemsWithLocalState];

    if (filter === "unread") {
      next = next.filter((n) => !n.readAt);
    }

    if (filter === "statusUpdates") {
      next = next.filter((n) =>
        isStatusUpdateNotification(n, parseMeta(n?.meta) || {})
      );
    }

    if (filter === "system") {
      next = next.filter(
        (n) => !isStatusUpdateNotification(n, parseMeta(n?.meta) || {})
      );
    }

    const q = searchQuery.trim().toLowerCase();

    if (q) {
      next = next.filter((n) => {
        const meta = parseMeta(n?.meta) || {};
        const params = getNotificationParams(meta);

        const searchableText = [
          n.title,
          n.body,
          n.notificationType,
          n.companyName,
          params.loadNumber,
          params.loadId,
          params.location,
          params.pointLocation,
          params.pickupInfo,
          params.deliveryInfo,
          params.actorName,
          params.updatedByName,
          params.newStatus,
          params.newStatusLabel,
          JSON.stringify(params),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(q);
      });
    }

    const getTime = (n) => {
      const d = new Date(n.createdAt || 0);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };

    next.sort((a, b) =>
      sortOrder === "oldest" ? getTime(a) - getTime(b) : getTime(b) - getTime(a)
    );

    return next;
  }, [itemsWithLocalState, filter, searchQuery, sortOrder]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setActiveNotificationId(null);
  }, [filter, searchQuery, sortOrder]);

  const hasMore = filteredItems.length > visibleCount;

  const visibleItems = useMemo(() => {
    return filteredItems.slice(0, visibleCount);
  }, [filteredItems, visibleCount]);

  const activeNotification = useMemo(() => {
    if (activeNotificationId === "__empty__") return null;

    if (activeNotificationId) {
      return (
        itemsWithLocalState.find(
          (n) => String(n.id) === String(activeNotificationId)
        ) ||
        visibleItems[0] ||
        null
      );
    }

    return visibleItems[0] || null;
  }, [itemsWithLocalState, visibleItems, activeNotificationId]);
  // se a lista encolher (ex: apagas/filtras), garante que não fica "a pedir" mais do que existe
  useEffect(() => {
    if (filter !== "all") return;
  
    setVisibleCount((prev) => {
      const total = filteredItems.length;
      if (total === 0) return PAGE_SIZE;
      return Math.min(Math.max(prev, PAGE_SIZE), total);
    });
  }, [filteredItems.length, filter]);
  const primaryCompanyName = useMemo(() => {
    const withName = itemsWithLocalState.find((n) => n.companyName);
    return withName?.companyName || null;
  }, [itemsWithLocalState]);

  const filterOptions = useMemo(
    () => [
      { value: "all", label: t("filters.all") },
      { value: "unread", label: t("filters.unread") },
      { value: "statusUpdates", label: t("filters.statusUpdates") },
      { value: "system", label: t("filters.system") },
    ],
    [t]
  );

  useEffect(() => {
    if (!filterMenuOpen) return;

    const handleClickOutside = (event) => {
      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target)
      ) {
        setFilterMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setFilterMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [filterMenuOpen]);
  const loadMore = useCallback(() => {
    setVisibleCount((prev) => {
      if (prev >= filteredItems.length) return prev;
      return Math.min(prev + PAGE_SIZE, filteredItems.length);
    });
  }, [filteredItems.length]);
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    const scrollRoot = listScrollRef.current;
  
    if (!sentinel) return;
    if (!hasMore) return;
  
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting) {
          loadMore();
        }
      },
      {
        // 👇 usa o container real da lista; se não existir, cai para window
        root: scrollRoot || null,
        rootMargin: "0px 0px 160px 0px",
        threshold: 0.01,
      }
    );
  
    observer.observe(sentinel);
  
    return () => {
      observer.unobserve(sentinel);
      observer.disconnect();
    };
  }, [hasMore, loadMore, visibleCount, filteredItems.length]);
  // 🔄 Sempre que a lista muda, sincronizar o contador global (Sidebar)
  useEffect(() => {
    try {
      window.dispatchEvent(
        new CustomEvent("dispatcher:notifs:unreadCount", {
          detail: { unread: notificationsActive ? unreadCount : 0 },
        })
      );
    } catch (e) {
      console.warn(
        "[DispatcherNotifications] falha ao emitir evento unreadCount",
        e
      );
    }
  }, [unreadCount, notificationsActive]);
  useEffect(() => {
    const runExpiryChecksOncePerDay = async () => {
      try {
        // ✅ se estiver OFF, não dispara checks nem faz fetch
        if (!notificationsActive) return;
        const todayStr = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
        const key = "expiryChecks.lastRun";
        const lastRun = localStorage.getItem(key);
        if (lastRun === todayStr) {
          // já foi corrido hoje → não faz nada
          return;
        }
        console.log(
          "[expiry-checks] primeiro arranque do dia → a disparar endpoint /maintenance/expiry-checks/run"
        );
        // ⚠️ Usa o "api" (axiosConfig) para garantir que bate no backend correto
        await api.post("/maintenance/expiry-checks/run");
        // marca como corrido hoje
        localStorage.setItem(key, todayStr);
        // 👇 Agora refaz o fetch às notificações para trazer as novas, sem piscar a página
        await fetchNotifications({ silent: true });
      } catch (err) {
        console.error("[expiry-checks] falha ao disparar checks via HTTP:", err);
      }
    };
    runExpiryChecksOncePerDay();
  }, [fetchNotifications, notificationsActive]);


const isPendingShipperConnectionRequest = useCallback((notif, meta = {}) => {
  if (!notif) return false;

  const normalizedType = normalizeNotificationType(notif.notificationType);

  const isConnectionRequest =
    normalizedType === "SHIPPERCONNECTIONREQUEST" ||
    normalizedType === "SHIPPERCONNECTIONINVITE" ||
    normalizedType === "SHIPPERCONNECTIONINVITATION";

  if (!isConnectionRequest) {
    return false;
  }

  const connectionStatus = String(
    meta.connectionStatus ||
      meta.status ||
      meta.partnerStatus ||
      ""
  )
    .trim()
    .toUpperCase();

  const responseAction = String(meta.responseAction || "")
    .trim()
    .toUpperCase();

  if (meta.actionRequired === false) return false;

  if (
    ["CONNECTED", "ACCEPTED", "REJECTED", "DECLINED"].includes(
      connectionStatus
    )
  ) {
    return false;
  }

  if (["ACCEPT", "REJECT", "DECLINE"].includes(responseAction)) {
    return false;
  }

  return true;
}, []);

const getConnectionResponseLabel = useCallback(
  (meta = {}) => {
    const connectionStatus = String(meta.connectionStatus || "")
      .trim()
      .toUpperCase();

    const responseAction = String(meta.responseAction || "")
      .trim()
      .toUpperCase();

    if (connectionStatus === "CONNECTED" || responseAction === "ACCEPT") {
      return t("connectionResponse.accepted", {
        defaultValue: "Accepted",
      });
    }

    if (
      connectionStatus === "REJECTED" ||
      responseAction === "REJECT" ||
      responseAction === "DECLINE"
    ) {
      return t("connectionResponse.declined", {
        defaultValue: "Declined",
      });
    }

    return null;
  },
  [t]
);

const handleShipperConnectionResponse = async (notif, action) => {
  if (!notif?.id) return;

  const normalizedAction = String(action || "").trim().toUpperCase();

  if (!["ACCEPT", "REJECT"].includes(normalizedAction)) {
    return;
  }

  const responseKey = `${notif.id}:${normalizedAction}`;

  setConnectionRespondingIds((prev) => {
    const next = new Set(prev);
    next.add(responseKey);
    return next;
  });

  try {
    const { data } = await api.post(
      `/notifications4dispatcher/${notif.id}/shipper-connection-response`,
      {
        action: normalizedAction,
      }
    );

    const updatedNotification = data?.notification || null;
    const updatedPartner = data?.partner || null;

    setItems((prev) =>
      prev.map((n) => {
        if (String(n.id) !== String(notif.id)) return n;

        if (updatedNotification) {
          return updatedNotification;
        }

        const oldMeta = parseMeta(n.meta) || {};

        return {
          ...n,
          readAt: n.readAt || new Date().toISOString(),
          meta: {
            ...oldMeta,
            actionRequired: false,
            respondedAt: new Date().toISOString(),
            responseAction: normalizedAction,
            connectionStatus:
              normalizedAction === "ACCEPT" ? "CONNECTED" : "REJECTED",
            partnerId: updatedPartner?.id || oldMeta.partnerId,
          },
        };
      })
    );

    updateLocalState((prev) => {
      const nextReadIds = new Set(prev.readIds);
      nextReadIds.delete(notif.id);

      return {
        ...prev,
        readIds: nextReadIds,
      };
    });
  } catch (e) {
    console.error(
      "[DispatcherNotifications] failed to respond to shipper connection request",
      e
    );

    window.alert(
      normalizedAction === "ACCEPT"
        ? t("connectionResponse.errors.accept", {
            defaultValue: "Could not accept this invitation.",
          })
        : t("connectionResponse.errors.decline", {
            defaultValue: "Could not decline this invitation.",
          })
    );
  } finally {
    setConnectionRespondingIds((prev) => {
      const next = new Set(prev);
      next.delete(responseKey);
      return next;
    });
  }
};

const translateStatusValue = useCallback(
  (value, baseNamespace = "dispatcher.load") => {
    if (value === null || value === undefined || value === "") return value;

    const raw = String(value).trim();
    if (!raw) return raw;

    const directUpper = raw.toUpperCase();
    const normalizedKey = normalizeStatusKey(raw);

    const aliasKey =
      STATUS_ALIAS_KEYS[directUpper] ||
      STATUS_ALIAS_KEYS[normalizedKey] ||
      null;

    const lookupKeys = Array.from(
      new Set(
        [
          directUpper,
          normalizedKey,
          aliasKey,
        ].filter(Boolean)
      )
    );

    const namespaces = Array.from(
      new Set(
        [
          baseNamespace,
          "dispatcher.pickupDelivery",
          "dispatcher.load",
          "dispatcher.invoice",
          "dispatcher.purchaseOrder",
        ].filter(Boolean)
      )
    );

    for (const namespace of namespaces) {
      for (const key of lookupKeys) {
        const translated = t(`${namespace}.statusLabels.${key}`, {
          defaultValue: "",
        });

        if (translated) {
          return translated;
        }
      }
    }

    for (const key of lookupKeys) {
      const fallbackLabel = getDefaultStatusLabel(key, i18n);
      if (fallbackLabel) {
        return fallbackLabel;
      }
    }

    if (/^[A-Z0-9_]+$/.test(raw)) {
      return raw
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    return raw;
  },
  [t, i18n]
);

  const translateStatusParams = useCallback(
    (params, baseNamespace = "dispatcher.load") => {
      if (!params || typeof params !== "object") return params;

      const statusFields = [
        "newStatus",
        "previousStatus",
        "status",
        "newStatusLabel",
        "previousStatusLabel",
        "statusLabel",
        "currentStatus",
        "currentStatusLabel",
        "oldStatus",
        "oldStatusLabel",
        "nextStatus",
        "nextStatusLabel",
        "pointStatus",
        "pointStatusLabel",
        "loadStatus",
        "loadStatusLabel",
        "driverStatus",
        "driverStatusLabel",
        "connectionStatus",
        "partnerStatus",
      ];

      statusFields.forEach((field) => {
        if (
          params[field] !== null &&
          params[field] !== undefined &&
          params[field] !== ""
        ) {
          params[field] = translateStatusValue(params[field], baseNamespace);
        }
      });

      // Garante compatibilidade com templates antigos/novos.
      if (params.newStatus && !params.newStatusLabel) {
        params.newStatusLabel = params.newStatus;
      }

      if (params.previousStatus && !params.previousStatusLabel) {
        params.previousStatusLabel = params.previousStatus;
      }

      if (params.status && !params.statusLabel) {
        params.statusLabel = params.status;
      }

      return params;
    },
    [translateStatusValue]
  );

  const translateStatusesInsideText = useCallback(
    (text, baseNamespace = "dispatcher.pickupDelivery") => {
      if (text === null || text === undefined || text === "") return text;

      let output = String(text);

      STATUS_TEXT_REPLACEMENTS.forEach(([rawText, statusKey]) => {
        const translated = translateStatusValue(statusKey, baseNamespace);

        if (!translated || translated === rawText) return;

        output = output.replace(
          new RegExp(`\\b${escapeRegExp(rawText)}\\b`, "gi"),
          translated
        );
      });

      return output;
    },
    [translateStatusValue]
  );

  const markNotificationAsReadOnOpen = useCallback(
    async (notif) => {
      if (!notif?.id || notif.readAt) return;

      const readAt = new Date().toISOString();

      // UI imediata
      setItems((prev) =>
        prev.map((n) =>
          String(n.id) === String(notif.id)
            ? { ...n, readAt: n.readAt || readAt }
            : n
        )
      );

      // fallback local caso o backend falhe
      updateLocalState((prev) => {
        const nextReadIds = new Set(prev.readIds);
        nextReadIds.add(notif.id);

        return {
          ...prev,
          readIds: nextReadIds,
        };
      });

      try {
        await api.patch(`/notifications4dispatcher/${notif.id}`, {
          read: true,
        });

        // backend atualizado, já não precisamos do override local
        updateLocalState((prev) => {
          const nextReadIds = new Set(prev.readIds);
          nextReadIds.delete(notif.id);

          return {
            ...prev,
            readIds: nextReadIds,
          };
        });
      } catch (e) {
        console.error(
          "[DispatcherNotifications] failed to mark notification as read on open",
          e
        );
      }
    },
    [updateLocalState]
  );

  const handleOpenNotification = useCallback(
    (notif) => {
      if (!notif?.id) return;

      setActiveNotificationId(notif.id);
      markNotificationAsReadOnOpen(notif);
    },
    [markNotificationAsReadOnOpen]
  );

  const handleToggleSelect = (id) => {
    setActiveNotificationId(id);

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedIds(() => new Set());
  };

  // ✅ Selecionar todas as notificações VISÍVEIS (respeita o filtro atual)
  const handleSelectAll = () => {
    setSelectedIds(() => {
      const next = new Set(visibleItems.map((n) => n.id));
      return next;
    });

    if (visibleItems[0]?.id) {
      setActiveNotificationId(visibleItems[0].id);
    }
  };

  // 👁️ Marcar todas as notificações visíveis como lidas
  const handleMarkVisibleAsRead = () => {
    const idsToUpdate = visibleItems.filter((n) => !n.readAt).map((n) => n.id);
    if (!idsToUpdate.length) return;

    updateLocalState((prev) => {
      const nextRead = new Set(prev.readIds);
      idsToUpdate.forEach((id) => nextRead.add(id));
      return { ...prev, readIds: nextRead };
    });
  };

  // 👁️ Marcar todas as selecionadas como lidas
  const handleMarkSelectedAsRead = () => {
    const idsToUpdate = Array.from(selectedIds);
    if (!idsToUpdate.length) return;

    updateLocalState((prev) => {
      const nextRead = new Set(prev.readIds);
      idsToUpdate.forEach((id) => nextRead.add(id));
      return { ...prev, readIds: nextRead };
    });
  };

  // ❌ Apagar apenas as selecionadas
  const handleBulkDelete = () => {
    const idsToDelete = Array.from(selectedIds);
    if (!idsToDelete.length) return;

    updateLocalState((prev) => {
      const nextHidden = new Set(prev.hiddenIds);
      idsToDelete.forEach((id) => nextHidden.add(id));
      return { ...prev, hiddenIds: nextHidden };
    });

    if (idsToDelete.some((id) => String(id) === String(activeNotificationId))) {
      setActiveNotificationId(null);
    }

    setSelectedIds(() => new Set());
  };

  // 🗑️ Apagar TODAS as notificações neste browser
  const handleDeleteAll = () => {
    if (!itemsWithLocalState.length) return;

    const ok = window.confirm(t("confirm.deleteAll"));
    if (!ok) return;

    const idsToDelete = items.map((n) => n.id);

    updateLocalState((prev) => {
      const nextHidden = new Set(prev.hiddenIds);
      idsToDelete.forEach((id) => nextHidden.add(id));
      return { ...prev, hiddenIds: nextHidden };
    });

    setSelectedIds(() => new Set());
    setActiveNotificationId(null);
  };

  // 👉 marcar como lida / não lida (optimistic UI)
  const handleToggleRead = async (notif) => {
    const nextRead = !notif.readAt;

    try {
      await api.patch(`/notifications4dispatcher/${notif.id}`, {
        read: nextRead,
      });

      setItems((prev) =>
        prev.map((n) =>
          n.id === notif.id
            ? { ...n, readAt: nextRead ? new Date().toISOString() : null }
            : n
        )
      );

      updateLocalState((prev) => {
        const nextReadIds = new Set(prev.readIds);
        nextReadIds.delete(notif.id); // limpa override local
        return { ...prev, readIds: nextReadIds };
      });
    } catch (e) {
      console.error("[DispatcherNotifications] failed to toggle read", e);
    }
  };

  // 👉 apagar notificação (single)
  const handleDelete = async (id) => {
    try {
      await api.delete(`/notifications4dispatcher/${id}`);

      setItems((prev) => prev.filter((n) => n.id !== id));

      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });

      if (String(activeNotificationId) === String(id)) {
        setActiveNotificationId(null);
      }

      updateLocalState((prev) => {
        const nextHidden = new Set(prev.hiddenIds);
        nextHidden.delete(id); // limpa override local
        return { ...prev, hiddenIds: nextHidden };
      });
    } catch (e) {
      console.error("[DispatcherNotifications] failed to delete notification", e);
    }
  };

  const resolveTitle = useCallback(
    (notif, rawMeta) => {
      const meta = rawMeta || {};
      // 1️⃣ Tentar formato NOVO (buildI18nMeta atual)
      let key = meta.i18nKey || null;
      let baseParams = meta.i18nParams || meta || {};
      // 2️⃣ Formato ANTIGO (meta.i18n.baseKey + args)
      if (!key && meta.i18n && meta.i18n.baseKey) {
        key = meta.i18n.baseKey;
        baseParams = meta.i18n.args || {};
      }
      // 3️⃣ Sem key → fallback para texto vindo do backend
      if (!key) {
        return notif.title && String(notif.title).trim()
          ? notif.title
          : t("card.fallbackTitle");
      }
      const params = normalizeNotificationDateParams(baseParams);

      // 🔍 Derivar o "namespace base" a partir da key
      // ex: "dispatcher.load.edited"  → "dispatcher.load"
      //     "dispatcher.pendentLoad.created" → "dispatcher.pendentLoad"
      let baseNamespace = "dispatcher.pendentLoad"; // fallback p/ compatibilidade
      if (key && typeof key === "string") {
        const parts = key.split(".");
        if (parts.length >= 2) {
          baseNamespace = `${parts[0]}.${parts[1]}`;
        }
      }
      // 🕒 Formatar datas se vierem em bruto
      if (params.deletedAt) {
        params.deletedAt = formatDateTime(params.deletedAt);
      }
      if (params.issueDate) {
        params.issueDate = formatDateTime(params.issueDate);
      }
      if (params.expiryDate) {
        params.expiryDate = formatDateTime(params.expiryDate);
      }
      if (params.assignedAt) {
        params.assignedAt = formatDateTime(params.assignedAt);
      }
      // 🆕 Calcular expirationText em função de daysBefore (TRUCK/TRAILER expiry)
      const isExpiryKey =
        key === "dispatcher.truck.inspectionExpiry" ||
        key === "dispatcher.truck.vrcExpiry" ||
        key === "dispatcher.truck.communityLicenceExpiry" ||
        key === "dispatcher.truck.insuranceExpiry" ||
        key === "dispatcher.trailer.inspectionExpiry" ||
        key === "dispatcher.trailer.vrcExpiry" ||
        key === "dispatcher.trailer.insuranceExpiry";
      if (isExpiryKey && typeof params.daysBefore === "number") {
        const daysBefore = params.daysBefore;
        const abs = Math.abs(daysBefore);
        let expirationText = null;
        if (daysBefore > 0) {
          // ainda falta X dias
          expirationText = t("dispatcher.common.expiry.inDays", {
            count: abs,
            days: abs,
          });
        } else if (daysBefore === 0) {
          // hoje
          expirationText = t("dispatcher.common.expiry.today");
        } else {
          // já expirou há X dias
          expirationText = t("dispatcher.common.expiry.daysAgo", {
            count: abs,
            days: abs,
          });
        }
        if (expirationText) {
          params.expirationText = expirationText;
        }
      }
      // 🌍 Traduzir typeKey -> typeLabel usando o namespace correto
      // ex: dispatcher.load.types.normal
      //     dispatcher.pendentLoad.types.normal
      if (params.typeKey) {
        params.typeLabel = t(`${baseNamespace}.types.${params.typeKey}`, {
          defaultValue: params.typeLabel || params.typeKey,
        });
      }

      translateStatusParams(params, baseNamespace);

      return t(`${key}.title`, {
        defaultValue:
          notif.title && String(notif.title).trim()
            ? notif.title
            : t("card.fallbackTitle"),
        ...params,
      });
    },
    [t, translateStatusParams]
  );

  const resolveBody = useCallback(
    (notif, rawMeta) => {
      const meta = rawMeta || {};
      let key = meta.i18nKey || null;
      let baseParams = meta.i18nParams || meta || {};
      if (!key && meta.i18n && meta.i18n.baseKey) {
        key = meta.i18n.baseKey;
        baseParams = meta.i18n.args || {};
      }
      // 👇 DEBUG só para veres o que vem nas notificações de histórico
      if (notif.notificationType === "LOAD_HISTORY_REVERTED") {
        console.log("[DEBUG loadHistory.reverted] meta=", meta);
        console.log("[DEBUG loadHistory.reverted] i18nKey usado no FE =", key);
      }
      if (!key) {
        return translateStatusesInsideText(
          notif.body ? String(notif.body) : "",
          "dispatcher.pickupDelivery"
        );
      }
      const params = normalizeNotificationDateParams(baseParams);

      // ✅ Overdue (PO/Invoice): converter ISO -> data bonita
      if (params.dueDateISO && !params.dueDate) {
        params.dueDate = formatDateOnly(params.dueDateISO);
      }
      // 👉 descobrir o "namespace base" a partir da key,
      // ex: "dispatcher.pendentLoad", "dispatcher.load", "dispatcher.pickupDelivery"
      let baseNamespace = "dispatcher.pendentLoad"; // fallback para compatibilidade
      if (key && typeof key === "string") {
        const parts = key.split(".");
        if (parts.length >= 2) {
          baseNamespace = `${parts[0]}.${parts[1]}`;
        }
      }
      // 🕒 Datas
      if (params.deletedAt) {
        params.deletedAt = formatDateTime(params.deletedAt);
      }
      if (params.issueDate) {
        params.issueDate = formatDateTime(params.issueDate);
      }
      if (params.expiryDate) {
        params.expiryDate = formatDateTime(params.expiryDate);
      }
      // ✅ Purchase Order overdue: format due date nicely (from ISO)
      if (params.dueDateISO && !params.dueDate) {
        // usa o mesmo formato curto (dd-mm-aaaa) para manter consistência na UI
        const onlyDate = (iso) => {
          const d = new Date(iso);
          if (Number.isNaN(d.getTime())) return "";
          const dia = String(d.getDate()).padStart(2, "0");
          const mes = String(d.getMonth() + 1).padStart(2, "0");
          const ano = d.getFullYear();
          return `${dia}-${mes}-${ano}`;
        };
        params.dueDate = onlyDate(params.dueDateISO);
      }
      // 🆕 Calcular expirationText em função de daysBefore (TRUCK/TRAILER expiry)
      const isExpiryKey =
        key === "dispatcher.truck.inspectionExpiry" ||
        key === "dispatcher.truck.vrcExpiry" ||
        key === "dispatcher.truck.communityLicenceExpiry" ||
        key === "dispatcher.truck.insuranceExpiry" ||
        key === "dispatcher.trailer.inspectionExpiry" ||
        key === "dispatcher.trailer.vrcExpiry" ||
        key === "dispatcher.trailer.insuranceExpiry";
      if (isExpiryKey && typeof params.daysBefore === "number") {
        const daysBefore = params.daysBefore;
        const abs = Math.abs(daysBefore);
        let expirationText = null;
        if (daysBefore > 0) {
          expirationText = t("dispatcher.common.expiry.inDays", {
            count: abs,
            days: abs,
          });
        } else if (daysBefore === 0) {
          expirationText = t("dispatcher.common.expiry.today");
        } else {
          expirationText = t("dispatcher.common.expiry.daysAgo", {
            count: abs,
            days: abs,
          });
        }
        if (expirationText) {
          params.expirationText = expirationText;
        }
      }
      // 🌍 Traduzir typeKey -> typeLabel, se existir
      if (params.typeKey) {
        params.typeLabel = t(`${baseNamespace}.types.${params.typeKey}`, {
          // se não houver tradução, cai para o que vier do backend ou para a key
          defaultValue: params.typeLabel || params.typeKey,
        });
      }
      // 🌍 Traduzir scope (pickup / delivery / ARRIVAL / DEPARTURE / ALL_PICKUPS / ALL_DELIVERIES)
      if (params.scope) {
        const rawScope = String(params.scope);
        const upperKey = rawScope.toUpperCase();
        // tenta primeiro UPPER, depois tal como veio; se nada existir, fica o valor original
        const scopeLabel = t(`dispatcher.pickupDelivery.scopes.${upperKey}`, {
          defaultValue: t(`dispatcher.pickupDelivery.scopes.${rawScope}`, {
            defaultValue: rawScope,
          }),
        });
        params.scope = scopeLabel;
      }
      // 🌍 Traduzir todos os campos de status conhecidos.
      // Isto evita mostrar enums brutos ou fallback em inglês-ish, ex:
      // WAITING_DELAYED_LATE_TRUCK -> A AGUARDAR — CAMIÃO ATRASADO
      translateStatusParams(params, baseNamespace);
      
      // 🔁 Se vierem campos alterados do backend, construir o resumo via i18n
      if (Array.isArray(params.changedFields) && params.changedFields.length > 0) {
        const fieldLabels = params.changedFields.map((fieldKey) =>
          t(`${baseNamespace}.fields.${fieldKey}`, {
            defaultValue: fieldKey,
          })
        );
        let summary;
        if (fieldLabels.length === 1) {
          summary = t(`${baseNamespace}.changesSummary.single`, {
            field: fieldLabels[0],
          });
        } else {
          const last = fieldLabels[fieldLabels.length - 1];
          const rest = fieldLabels.slice(0, -1);
          summary = t(`${baseNamespace}.changesSummary.multiple`, {
            fields: rest.join(", "),
            last,
          });
        }
        // 👇 sobrepõe o que viesse do backend
        params.changesSummary = summary;
      }
      const resolvedBody = t(`${key}.body`, {
        defaultValue: notif.body ? String(notif.body) : "",
        ...params,
      });

      return translateStatusesInsideText(resolvedBody, baseNamespace);
    },
    [t, translateStatusParams, translateStatusesInsideText]
  );

  // 👇 Resolve o "badge" do tipo usando i18n
  const resolveTypeLabel = useCallback(
    (notificationType) => {
      if (!notificationType) return null;

      const raw = String(notificationType);
      const normalizedType = normalizeNotificationType(raw);

      if (
        raw.startsWith("TRUCK_INSPECTION_PHOTO_") ||
        normalizedType.startsWith("TRUCKINSPECTIONPHOTO")
      ) {
        return t("types.TRUCK_INSPECTION_PHOTO");
      }

      if (
        raw.startsWith("TRUCK_VRC_") ||
        normalizedType.startsWith("TRUCKVRC")
      ) {
        return t("types.TRUCK_VRC");
      }

      if (
        raw.startsWith("TRUCK_CL_") ||
        normalizedType.startsWith("TRUCKCL") ||
        normalizedType.startsWith("TRUCKCOMMUNITYLICENCE")
      ) {
        return t("types.TRUCK_CL");
      }

      if (
        raw.startsWith("TRUCK_INSURANCE_EXPIRY") ||
        normalizedType.startsWith("TRUCKINSURANCEEXPIRY")
      ) {
        return t("types.TRUCK_INSURANCE_EXPIRY");
      }

      if (
        raw.startsWith("TRAILER_INSPECTION_PHOTO_") ||
        normalizedType.startsWith("TRAILERINSPECTIONPHOTO")
      ) {
        return t("types.TRAILER_INSPECTION_PHOTO");
      }

      if (
        raw.startsWith("TRAILER_VRC_") ||
        normalizedType.startsWith("TRAILERVRC")
      ) {
        return t("types.TRAILER_VRC");
      }

      if (
        raw.startsWith("TRAILER_INSURANCE_EXPIRY") ||
        normalizedType.startsWith("TRAILERINSURANCEEXPIRY")
      ) {
        return t("types.TRAILER_INSURANCE_EXPIRY");
      }

      if (normalizedType === "TRAILERASSIGNMENT") {
        return t("types.TRAILER_ASSIGNMENT", {
          defaultValue: "Reboque atribuído",
        });
      }

      if (normalizedType === "TRAILERUNASSIGNMENT") {
        return t("types.TRAILER_UNASSIGNMENT", {
          defaultValue: "Reboque removido",
        });
      }

      if (normalizedType === "SHIPPERCONNECTIONREQUEST") {
        return t("types.SHIPPERCONNECTIONREQUEST", {
          defaultValue: "Pedido de ligação de expedidor",
        });
      }

      const fallback = raw.replace(/_/g, " ").toLowerCase();

      return t(`types.${raw}`, {
        defaultValue: fallback,
      });
    },
    [t]
  );

  const activeMeta = activeNotification
    ? parseMeta(activeNotification?.meta) || {}
    : {};

  const activeTitle = activeNotification
    ? resolveTitle(activeNotification, activeMeta)
    : "";

  const activeBody = activeNotification
    ? resolveBody(activeNotification, activeMeta)
    : "";

  const activeLoadNumber = activeNotification
    ? getNotificationLoadNumber(activeNotification, activeMeta)
    : null;

  const translatePointTypeValue = useCallback(
    (value) => {
      if (!value) return null;

      const raw = String(value).trim();
      const normalizedKey = normalizeStatusKey(raw);

      return t(`dispatcher.pickupDelivery.pointTypes.${normalizedKey}`, {
        defaultValue: t(`dispatcher.pickupDelivery.scopes.${normalizedKey}`, {
          defaultValue: raw,
        }),
      });
    },
    [t]
  );

  const activeRawPointType = activeNotification
    ? getNotificationPointType(activeMeta)
    : null;

  const activePointType = activeRawPointType
    ? translatePointTypeValue(activeRawPointType)
    : null;

  const activeLocation = activeNotification
    ? getNotificationLocation(activeMeta)
    : null;

  const activeRawStatus = activeNotification
    ? getNotificationNewStatus(activeMeta)
    : null;

  const activeNewStatus = activeRawStatus
    ? translateStatusValue(activeRawStatus, "dispatcher.pickupDelivery")
    : null;

  const activeResponsible = activeNotification
    ? getNotificationResponsible(activeMeta)
    : null;

  const activeTypeLabel = activeNotification?.notificationType
    ? resolveTypeLabel(activeNotification.notificationType)
    : null;

  const activeNotificationVisual = activeNotification
    ? getNotificationVisual(activeNotification.notificationType)
    : getNotificationVisual(null);

  const ActivePreviewIcon = activeNotificationVisual.Icon;

  if (initialLoading) {

    return (
      <div className="notifications-page">
        <div className="notifications-header">
          <div className="notifications-header-main">
            <h1>{t("page.title")}</h1>
          </div>
        </div>
        <div className="notifications-state notifications-state--loading">
          <div className="notifications-skeleton-line" />
          <div className="notifications-skeleton-line notifications-skeleton-line--short" />
        </div>
      </div>
    );
  }
  if (err) {

    return (
      <div className="notifications-page">
        <div className="notifications-header">
          <div className="notifications-header-main">
            <h1>{t("page.title")}</h1>
            {primaryCompanyName && (
              <span className="notifications-company-tag">
                {primaryCompanyName}
              </span>
            )}
          </div>
        </div>
        <div className="notifications-state notifications-state--error">
          {t(`errors.${err}`, { defaultValue: t("errors.generic") })}
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <section className="notifications-shell">
        <div className="notifications-hero">
          <div className="notifications-hero-main">

            <div>
              <h1>{t("page.title")}</h1>
              <p>{t("page.subtitle")}</p>

              {primaryCompanyName && (
                <span className="notifications-company-tag">
                  {primaryCompanyName}
                </span>
              )}
            </div>
          </div>

          <div className="notifications-hero-side">
            <div className="notifications-hero-topline">
              <div
                className="notifications-filter-tabs notifications-filter-tabs--hero"
                aria-label={t("filters.label")}
              >
                <button
                  type="button"
                  className={`notifications-filter-btn ${
                    filter === "all" ? "notifications-filter-btn--active" : ""
                  }`}
                  onClick={() => setFilter("all")}
                >
                  <span>{t("filters.all")}</span>
                  <strong>{itemsWithLocalState.length}</strong>
                </button>

                <button
                  type="button"
                  className={`notifications-filter-btn ${
                    filter === "unread" ? "notifications-filter-btn--active" : ""
                  }`}
                  onClick={() => setFilter("unread")}
                >
                  <span>{t("filters.unread")}</span>
                  <strong>{unreadCount}</strong>
                </button>
              </div>

              <span className="notifications-showing-copy">
                {t("list.showing", {
                  shown: Math.min(visibleItems.length, filteredItems.length),
                  total: filteredItems.length,
                })}
              </span>
            </div>

            <div className="notifications-hero-controls">
              <label className="notifications-search">
                <FiSearch />
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("search.placeholder")}
                />
              </label>

              <div
                ref={filterMenuRef}
                className={`notifications-filter-menu ${
                  filterMenuOpen ? "notifications-filter-menu--open" : ""
                }`}
              >
                <button
                  type="button"
                  className="notifications-filter-trigger"
                  onClick={() => setFilterMenuOpen((prev) => !prev)}
                  aria-label={t("filters.label")}
                  aria-expanded={filterMenuOpen}
                >
                  <FiFilter />
                  <span>{t("filters.short")}</span>
                  <FiChevronDown />
                </button>

                {filterMenuOpen && (
                  <div className="notifications-filter-dropdown">
                    {filterOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`notifications-filter-option ${
                          filter === option.value
                            ? "notifications-filter-option--active"
                            : ""
                        }`}
                        onClick={() => {
                          setFilter(option.value);
                          setFilterMenuOpen(false);
                        }}
                      >
                        <span>{option.label}</span>
                        {filter === option.value && <FiCheckCircle />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="notifications-layout">
          <section className="notifications-list-panel">
          {selectedIds.size === 0 ? (
              <div className="notifications-toolbar notifications-toolbar--in-panel">
                <div className="notifications-toolbar-left">
                  <button
                    type="button"
                    className="notifications-bulk-btn"
                    onClick={handleSelectAll}
                    disabled={!visibleItems.length}
                  >
                    <FiCheckCircle />
                    <span>{t("bulk.selectAll")}</span>
                  </button>

                  <button
                    type="button"
                    className="notifications-bulk-btn"
                    onClick={handleMarkVisibleAsRead}
                    disabled={!visibleItems.some((n) => !n.readAt)}
                  >
                    <FiMail />
                    <span>{t("bulk.markVisibleAsRead")}</span>
                  </button>

                  <button
                    type="button"
                    className="notifications-bulk-btn notifications-bulk-btn--danger-strong"
                    onClick={handleDeleteAll}
                    disabled={!itemsWithLocalState.length}
                  >
                    <FiTrash2 />
                    <span>{t("bulk.deleteAll")}</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="notifications-selection-bar">
                <span>
                  {selectedIds.size === 1
                    ? t("bulk.selectedSingle")
                    : t("bulk.selectedMultiple", { count: selectedIds.size })}
                </span>

                <div>
                  <button
                    type="button"
                    className="notifications-bulk-btn"
                    onClick={handleMarkSelectedAsRead}
                  >
                    <FiEye />
                    <span>{t("bulk.markSelectedAsRead")}</span>
                  </button>

                  <button
                    type="button"
                    className="notifications-bulk-btn notifications-bulk-btn--danger"
                    onClick={handleBulkDelete}
                  >
                    <FiTrash2 />
                    <span>{t("bulk.deleteSelected")}</span>
                  </button>

                  <button
                    type="button"
                    className="notifications-bulk-btn"
                    onClick={handleClearSelection}
                  >
                    {t("bulk.clearSelection")}
                  </button>
                </div>
              </div>
            )}

            {filteredItems.length === 0 ? (
              <div className="notifications-state notifications-state--empty">
                {filter === "unread" ? t("empty.unread") : t("empty.all")}
              </div>
            ) : (
              <>
                <div ref={listScrollRef} className="notifications-list">
                  {visibleItems.map((n) => {
                    const unread = !n.readAt;
                    const isSelected = selectedIds.has(n.id);
                    const isActive =
                      activeNotification &&
                      String(activeNotification.id) === String(n.id);

                    const meta = parseMeta(n?.meta) || {};
                    const resolvedTitle = resolveTitle(n, meta || {});
                    const resolvedBody = resolveBody(n, meta || {});
                    const hasBody =
                      resolvedBody && String(resolvedBody).trim().length > 0;
                    const plateLabel = meta?.plate || meta?.truckPlate || null;
                    const loadNumber = getNotificationLoadNumber(n, meta);

                    const isPendingConnectionInvite =
                      isPendingShipperConnectionRequest(n, meta);
                    const connectionResponseLabel = getConnectionResponseLabel(meta);

                    const acceptingThis = connectionRespondingIds.has(
                      `${n.id}:ACCEPT`
                    );
                    const decliningThis = connectionRespondingIds.has(
                      `${n.id}:REJECT`
                    );
                    const respondingThis = acceptingThis || decliningThis;
                    const notificationVisual = getNotificationVisual(n.notificationType);
                    const NotificationIcon = notificationVisual.Icon;

                    return (
                      <article
                        key={n.id}
                        className={`notification-card
                          ${unread ? "notification-card--unread" : ""}
                          ${isSelected ? "notification-card--selected" : ""}
                          ${isActive ? "notification-card--active" : ""}
                        `}
                        onClick={() => handleOpenNotification(n)}
                      >
                        <span className="notification-card-dot" />

                        <div
                          className={`notification-card-icon notification-card-icon--${notificationVisual.variant}`}
                        >
                          <NotificationIcon />
                        </div>

                        <div className="notification-card-content">
                          <div className="notification-card-title-row">
                            <h2 className="notification-title">{resolvedTitle}</h2>

                            {loadNumber && (
                              <span className="notification-load-pill">
                                {t("card.loadPill", { loadNumber })}
                              </span>
                            )}
                          </div>

                          <div className="notification-meta">
                            {n.createdAt && (
                              <span className="notification-meta-item">
                                <FiClock />
                                {formatDateTime(n.createdAt)}
                              </span>
                            )}

                            {n.notificationType && (
                              <span className="notification-type-tag">
                                {resolveTypeLabel(n.notificationType)}
                              </span>
                            )}

                            {plateLabel && (
                              <span className="notification-meta-item">
                                {t("card.truckLabel", { plate: plateLabel })}
                              </span>
                            )}

                            {n.companyName && (
                              <span className="notification-meta-item">
                                {n.companyName}
                              </span>
                            )}
                          </div>

                          {hasBody && (
                            <p className="notification-body">
                              {String(resolvedBody)}
                            </p>
                          )}

                          {isPendingConnectionInvite && (
                            <div
                              className="notification-connection-actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                className="notification-connection-btn notification-connection-btn--accept"
                                disabled={respondingThis}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShipperConnectionResponse(n, "ACCEPT");
                                }}
                              >
                                <FiCheckCircle />
                                <span>
                                  {acceptingThis
                                    ? t("connectionResponse.accepting", {
                                        defaultValue: "Accepting...",
                                      })
                                    : t("connectionResponse.accept", {
                                        defaultValue: "Accept",
                                      })}
                                </span>
                              </button>

                              <button
                                type="button"
                                className="notification-connection-btn notification-connection-btn--decline"
                                disabled={respondingThis}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShipperConnectionResponse(n, "REJECT");
                                }}
                              >
                                <FiXCircle />
                                <span>
                                  {decliningThis
                                    ? t("connectionResponse.declining", {
                                        defaultValue: "Declining...",
                                      })
                                    : t("connectionResponse.decline", {
                                        defaultValue: "Decline",
                                      })}
                                </span>
                              </button>
                            </div>
                          )}

                          {connectionResponseLabel && !isPendingConnectionInvite && (
                            <div className="notification-connection-result">
                              {connectionResponseLabel}
                            </div>
                          )}
                        </div>

                        <div
                          className="notification-actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="notification-action-btn"
                            title={
                              unread
                                ? t("card.tooltipMarkAsRead")
                                : t("card.tooltipMarkAsUnread")
                            }
                            onClick={() => handleToggleRead(n)}
                          >
                            {unread ? <FiEye /> : <FiEyeOff />}
                          </button>

                          <button
                            type="button"
                            className="notification-action-btn notification-action-btn--danger"
                            title={t("card.tooltipDelete")}
                            onClick={() => handleDelete(n.id)}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </article>
                    );
                  })}

                  <div
                    ref={loadMoreRef}
                    className="notifications-load-more-sentinel"
                  />
                </div>

                <div className="notifications-list-footer">
                  <span>
                    {t("list.showingRange", {
                      from: filteredItems.length ? 1 : 0,
                      to: Math.min(visibleItems.length, filteredItems.length),
                      total: filteredItems.length,
                    })}
                  </span>

                  {hasMore && (
                    <button
                      type="button"
                      className="notifications-load-more-btn"
                      onClick={loadMore}
                    >
                      {t("list.loadMore", { defaultValue: "Load more" })}
                    </button>
                  )}
                </div>
              </>
            )}
          </section>

          <aside className="notification-preview-panel">
            {activeNotification ? (
              <>
                <div className="notification-preview-top">
                  <span>{t("preview.selected")}</span>

                  <button
                    type="button"
                    className="notification-preview-close"
                    onClick={() => setActiveNotificationId("__empty__")}
                  >
                    <FiX />
                  </button>
                </div>

                <div className="notification-preview-heading">
                  <div
                    className={`notification-preview-icon notification-card-icon--${activeNotificationVisual.variant}`}
                  >
                    <ActivePreviewIcon />
                  </div>

                  <div>
                    <div className="notification-preview-title-row">
                      <h2>{activeTitle}</h2>
                    </div>

                    <div className="notification-preview-pills">
                      {activeNotification.createdAt && (
                        <span className="notification-meta-item">
                          <FiClock />
                          {formatDateTime(activeNotification.createdAt)}
                        </span>
                      )}

                      <span
                        className={`notification-read-state ${
                          activeNotification.readAt ? "notification-read-state--read" : ""
                        }`}
                      >
                        {activeNotification.readAt
                          ? t("preview.read")
                          : t("preview.unread")}
                      </span>

                      {activeTypeLabel && (
                        <span className="notification-type-tag">
                          {activeTypeLabel}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="notification-preview-section">
                  <span className="notification-preview-label">
                    {t("preview.message")}
                  </span>

                  <p>{activeBody || activeTitle}</p>
                </div>

                <div className="notification-preview-section">
                  <span className="notification-preview-label">
                    {t("preview.detailsPoint")}
                  </span>

                  <div className="notification-preview-details">
                    {activeLoadNumber && (
                      <div>
                        <span>
                          <FiBox />
                          {t("preview.load")}
                        </span>
                        <strong>{activeLoadNumber}</strong>
                      </div>
                    )}

                    {activeLocation && (
                      <div>
                        <span>
                          <FiMapPin />
                          {t("preview.location")}
                        </span>
                        <strong>{activeLocation}</strong>
                      </div>
                    )}

                    {activePointType && (
                      <div>
                        <span>
                          <LuTruck />
                          {t("preview.pointType")}
                        </span>
                        <strong>{activePointType}</strong>
                      </div>
                    )}

                    {activeNewStatus && (
                      <div>
                        <span>
                          <FiClock />
                          {t("preview.newStatus")}
                        </span>
                        <strong className="notification-preview-status">
                          {activeNewStatus}
                        </strong>
                      </div>
                    )}

                    {activeResponsible && (
                      <div>
                        <span>
                          <FiUser />
                          {t("preview.responsible")}
                        </span>
                        <strong>{activeResponsible}</strong>
                      </div>
                    )}
                  </div>
                </div>

                <div className="notification-preview-section">
                  <span className="notification-preview-label">
                    {t("preview.activity")}
                  </span>

                  <div className="notification-preview-activity">
                    <div className="notification-preview-activity-item notification-preview-activity-item--active">
                      <span className="notification-preview-activity-dot" />
                      <div>
                        <strong>
                          {activeNotification.createdAt
                            ? formatDateTime(activeNotification.createdAt)
                            : t("preview.now")}
                        </strong>
                        <p>
                          {activeNewStatus
                            ? t("preview.activityStatus", {
                                status: activeNewStatus,
                                actorName: activeResponsible || "",
                              })
                            : activeTitle}
                        </p>
                      </div>
                    </div>

                    {activeNewStatus && (
                      <div className="notification-preview-activity-item">
                        <span className="notification-preview-activity-dot" />
                        <div>
                          <strong>{t("preview.previousState")}</strong>
                          <p>{t("preview.previousStateBody")}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="notification-preview-empty">
                <FiBell />
                <h2>{t("preview.emptyTitle")}</h2>
                <p>{t("preview.emptyBody")}</p>
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
}
export default DispatcherNotifications;
